import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { supplementProviderApi } from "apis";
import { useToast } from "contexts/ToastContext";
import {
  FormulaResponseDto,
  GetFormulasInPrescriptionRequest,
  GetSupplementProviderFormulasRequest,
  GetSupplementProviderMaterialsRequest,
  RemoveFormulaFromPrescriptionRequest,
} from "libs/cellera-sdk";
import { CreateFormulaRequest } from "types/formula";
import { MaterialsResponse } from "types/materials";

export const useGetFormulas = (
  params: GetSupplementProviderFormulasRequest
) => {
  return useQuery<FormulaResponseDto[]>({
    queryKey: ["formulas", params],
    queryFn: () => supplementProviderApi.getSupplementProviderFormulas(params),
    placeholderData: keepPreviousData,
  });
};

export const useGetFormulasInPrescription = (
  params: GetFormulasInPrescriptionRequest
) => {
  return useQuery({
    queryKey: ["formulasInPrescription", params],
    queryFn: () => supplementProviderApi.getFormulasInPrescription(params),
    placeholderData: keepPreviousData,
    enabled: !!params.prescriptionId,
  });
};

export const useGetMaterials = (
  params: GetSupplementProviderMaterialsRequest
) => {
  return useQuery<MaterialsResponse>({
    queryKey: ["materials", params],
    queryFn: () =>
      supplementProviderApi.getSupplementProviderMaterials(
        params
      ) as Promise<MaterialsResponse>,
    placeholderData: keepPreviousData,
  });
};

export const useCreateFormula = () => {
  const { showError } = useToast();
  return useMutation({
    mutationFn: (params: CreateFormulaRequest) =>
      supplementProviderApi.createSupplementProviderFormula({
        body: params,
      }) as Promise<FormulaResponseDto>,
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Create formula failed. Please try again.";
      showError("Create formula failed", errorMessage);
    },
  });
};

export const useRemoveFormulaFromPrescription = () => {
  const { showError } = useToast();
  return useMutation({
    mutationFn: (params: RemoveFormulaFromPrescriptionRequest) =>
      supplementProviderApi.removeFormulaFromPrescription(params),
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Remove formula from prescription failed. Please try again.";
      showError("Remove formula from prescription failed", errorMessage);
    },
  });
};
